<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faEllipsis, faTrashCan, faCircleCheck } from '@fortawesome/pro-light-svg-icons';
  
  const API_BASE_URL = '/api/proxy';
  
  import { formatToLocalDateTime } from '$utils/dateUtils.js';

  let onDemandRequests = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;

  let showMenu = false;
  let menuItem = null;
  let menuPosition = { top: 0, left: 0, direction: 'down' };

  let showConfirmation = false;
  let itemToDelete = null;

  onMount(async () => {
    if ($selectedParticipant) {
      fetchOnDemandRequests(1);
    }
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  async function fetchOnDemandRequests(page = 1) {
    try {
      let url = `${API_BASE_URL}/onDemandRequests?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch on-demand requests');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      onDemandRequests = responseData.data;
    } catch (err) {
      console.error('Error fetching on-demand requests:', err);
      onDemandRequests = [];
    }
  }

  function handleRefresh() {
    fetchOnDemandRequests(currentPage);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchOnDemandRequests(newPage);
    }
  }

  function handleMenuClick(item, event) {
    event.stopPropagation();
    menuItem = item;
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 100;
    const menuWidth = 192;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    let direction = 'down';
    let top = buttonRect.bottom + window.scrollY;
    let left = buttonRect.left + window.scrollX;
    if (buttonRect.bottom + menuHeight > viewportHeight) {
      direction = 'up';
      top = buttonRect.top + window.scrollY - menuHeight;
      if (top < 0) top = 0;
    }
    if (left + menuWidth > viewportWidth - 8) {
      left = viewportWidth - menuWidth - 8;
      if (left < 0) left = 0;
    }
    menuPosition = { top, left, direction };
    showMenu = true;
  }

  function handleMenuClose() {
    showMenu = false;
    menuItem = null;
  }

  function handleClickOutside(event) {
    if (showMenu && !event.target.closest('.menu-container')) {
      handleMenuClose();
    }
  }

  function handleDeleteClick(item) {
    itemToDelete = item;
    showConfirmation = true;
  }

  async function confirmDelete() {
    if (itemToDelete) {
      try {
        const res = await fetch(`${API_BASE_URL}/onDemandRequests/${itemToDelete._id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
          throw new Error('Failed to delete on-demand request');
        }

        await fetchOnDemandRequests(currentPage);
        showConfirmation = false;
        itemToDelete = null;
      } catch (err) {
        console.error('Error deleting on-demand request:', err);
      }
    }
  }

  async function handleMarkFilled(item) {
    try {
      const res = await fetch(`${API_BASE_URL}/onDemandRequests/${item._id}/filled`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) {
        throw new Error('Failed to mark on-demand request as filled');
      }

      await fetchOnDemandRequests(currentPage);
    } catch (err) {
      console.error('Error marking on-demand request as filled:', err);
    }
  }
</script>

<ConfirmationModal
  {showConfirmation}
  message={`Are you sure you want to delete this on-demand request: ${itemToDelete?.name || 'Unknown'}?`}
  on:confirm={confirmDelete}
  on:close={() => (showConfirmation = false)}
/>

<StudyHeader />

<FeatureInfo 
  title="On Demand Requests" 
  description="This page displays on-demand requests made by the selected participant. You can view when requests were made 
  and whether they have been fulfilled. This helps track when participants have been asked to take additional measurements."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="On Demand Requests" itemCountTitle="Requests" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="text-black dark:text-white">
      <button on:click={handleRefresh} class="pagination mx-1 p-0">
        <FontAwesomeIcon icon={faRotate} />
      </button>
    </div>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold">Date (Local)</th>
        <th class="px-4 py-2 text-left bold">Request</th>
        <th class="px-4 py-2 text-left bold">Filled</th>
        <th class="px-4 py-2 text-left bold w-12"></th>
      </tr>
    </thead>
    <tbody>
      {#each onDemandRequests as onDemandRequest, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2"
            >{onDemandRequest.createdDate
              ? formatToLocalDateTime(
                  onDemandRequest.createdDate,
                  $selectedParticipant.timezoneCode
                )
              : ''}</td
          >
          <td class="px-4 py-2">{onDemandRequest.name}</td>
          <td class="px-4 py-2">{onDemandRequest.filled}</td>
          <td class="px-4 py-2">
            <div class="menu-container relative">
              <button
                on:click={(e) => handleMenuClick(onDemandRequest, e)}
                class="pagination mx-1 p-0"
                title="More options"
              >
                <FontAwesomeIcon icon={faEllipsis} class="text-sky-800 dark:text-white/70" />
              </button>
              {#if showMenu && menuItem?._id === onDemandRequest._id}
                <div
                  class="fixed w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 menu-dropdown"
                  style="top: {menuPosition.top}px; left: {menuPosition.left}px; max-height: 200px; overflow-y: auto;"
                >
                  <button
                    on:click={() => {
                      handleMarkFilled(onDemandRequest);
                      handleMenuClose();
                    }}
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    disabled={onDemandRequest.filled === true}
                    class:opacity-50={onDemandRequest.filled === true}
                    title={onDemandRequest.filled === true ? 'Already marked as filled' : 'Mark as filled'}
                  >
                    <span>Mark as filled</span>
                    <FontAwesomeIcon icon={faCircleCheck} class="text-sky-800 dark:text-white/70" />
                  </button>
                  <button
                    on:click={() => {
                      handleDeleteClick(onDemandRequest);
                      handleMenuClose();
                    }}
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  >
                    <span>Delete request</span>
                    <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                  </button>
                </div>
              {/if}
            </div>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>
